/*
 * Pure logic for the "Join now" pop-up: which prompt shows next (one at a
 * time), its copy, where "Join session" goes, and who polls at all.
 */

import type { SalesMe } from '../../sales_team/api';
import { fmtTime, studentLocal } from './allocation';
import type { JoinNowItem, PromptResult } from './types';

export const JOIN_POLL_MS = 30 * 1000;
// focus and visibilitychange both fire on one return to the tab; fetch once.
export const REFETCH_MIN_MS = 5 * 1000;

export const shouldRefetch = (lastAt: number, now: number): boolean =>
  now - lastAt >= REFETCH_MIN_MS;

interface FocusTarget {
  tagName?: string;
  isContentEditable?: boolean;
}

// Never pull focus from someone typing or from an open dialog; the pop-up is announced instead.
export const mayTakeFocus = (
  active: FocusTarget | null | undefined,
  dialogOpen = false
): boolean => {
  if (dialogOpen) return false;
  if (!active) return true;
  const tag = (active.tagName || '').toUpperCase();
  return !active.isContentEditable && tag !== 'INPUT' && tag !== 'TEXTAREA' && tag !== 'SELECT';
};

const toMs = (iso: string): number => Date.parse(iso);

// Only roster members can be allocated or hold the task, so nobody else polls.
export const mayPollJoinNow = (me: SalesMe | null | undefined): boolean =>
  !!me && (!!me.level || me.can_claim === true);

// Earliest join first; handled = answered here, kept until the server stops listing it.
// One prompt per (session, role, allocation): a re-allocated rep is prompted again in the same tab.
// allocated_name stands in for allocated_at on a server that does not send it.
export const promptKey = (item: JoinNowItem): string =>
  [item.session_uuid, item.role, item.allocated_at || item.allocated_name || ''].join('|');

export const nextPrompt = (
  items: JoinNowItem[],
  handled: ReadonlySet<string>,
  now: number
): JoinNowItem | null => {
  const open = items
    .filter((i) => !handled.has(promptKey(i)) && !(toMs(i.end_at) <= now))
    .sort(
      (a, b) => toMs(a.join_at) - toMs(b.join_at) || a.session_uuid.localeCompare(b.session_uuid)
    );
  return open[0] || null;
};

// Prompts the server no longer lists can be forgotten, so the set stays small.
export const pruneHandled = (handled: ReadonlySet<string>, items: JoinNowItem[]): Set<string> => {
  const live = new Set(items.map(promptKey));
  return new Set([...handled].filter((id) => live.has(id)));
};

export interface PromptCopy {
  title: string;
  when: string;
  reason: string;
  task_line: string;
  student_time: string;
}

export const promptCopy = (item: JoinNowItem, now: number): PromptCopy => {
  const holder = item.role === 'holder';
  let reason = "You are allocated to join this session's last 10 minutes and talk membership.";
  if (holder) {
    reason = item.allocated_name
      ? `You hold this session's task. ${item.allocated_name} is allocated to join; join too only if they can't.`
      : "You hold this session's task. Join its last 10 minutes and talk membership.";
  }
  let taskLine = 'Session joining & closing, held by you';
  if (!holder) {
    if (!item.task_uuid) taskLine = 'No session task yet';
    else if (item.holder_name) taskLine = `Session joining & closing, held by ${item.holder_name}`;
    else taskLine = 'Session joining & closing, unclaimed';
  }
  return {
    title: `${item.student_name}: trial ends at ${fmtTime(item.end_at)}`,
    when: `${fmtTime(item.join_at)} · last 10 minutes`,
    reason,
    task_line: taskLine,
    student_time: studentLocal(item.student_tz, now)
  };
};

// Session details carries the existing "Join as care" control; there is no server-side join link.
export const sessionHref = (sessionUuid: string): string =>
  `/sessiondetails?id=${encodeURIComponent(sessionUuid)}`;

export interface JoinLinks {
  join: string;
  task: string | null;
}

// join_url wins if the server ever sends one; today it is always null.
export const joinLinks = (item: JoinNowItem): JoinLinks => ({
  join: item.join_url || sessionHref(item.session_uuid),
  task: item.task_uuid ? `/sales-task-form?uuid=${encodeURIComponent(item.task_uuid)}` : null
});

export interface JoinStatus {
  text: string;
  tone: 'success' | 'neutral' | 'warning';
  task_href: string | null;
}

const taskHref = (uuid: string | null | undefined): string | null =>
  uuid ? `/sales-task-form?uuid=${encodeURIComponent(uuid)}` : null;

// After "Join session": what the claim did. null = nothing to say (holder role, no task, closed).
// A null result is a failed request, shown like a failed claim.
export const joinStatus = (result: PromptResult | null, item: JoinNowItem): JoinStatus | null => {
  if (item.role !== 'allocated') return null;
  const href = taskHref(result?.task_uuid || item.task_uuid);
  if (result?.claimed) {
    const rel = result.released_task;
    // Mirrors the form's claim toast: the instant claim let go of another held task.
    const released = rel
      ? ` Your held ${rel.task_label}${rel.student_name ? ` for ${rel.student_name}` : ''} went back to the queue.`
      : '';
    return {
      text: `Task claimed. Record the outcome from Open task after the call.${released}`,
      tone: 'success',
      task_href: href
    };
  }
  const reason = result ? result.claim_reason : 'failed';
  if (reason === 'held_by_other') {
    return {
      text: `${item.holder_name || 'Another rep'} holds the task.`,
      tone: 'neutral',
      task_href: href
    };
  }
  if (reason === 'holding') {
    return {
      text: "Not claimed: you're holding another task. Finish or release it, then open this one.",
      tone: 'warning',
      task_href: href
    };
  }
  if (reason === 'not_sales') {
    return {
      text: "Not claimed: your account can't claim sales tasks.",
      tone: 'warning',
      task_href: null
    };
  }
  if (reason === 'failed') {
    return { text: "Couldn't claim the task. Open it to claim.", tone: 'warning', task_href: href };
  }
  return null;
};
